import React, { useEffect, useRef, useCallback } from 'react';
import useNotificationsStore from '@/stores/notifications/notifications';
import ChevronRightIcon from '@/shared/icons/ChevronRightIcon';
import NotificationIcon from '@/shared/icons/NotificationIcon';
import * as notificationService from '@/services/notification.service';
import { DateTime } from 'luxon';
import CloseIconMenu from '@/shared/icons/CloseIconMenu';
import IconWrapper from '@/shared/components/UI/IconWrapper';

const DNotificationBlockUpdates = ({ fetchMore, loadingMore, hasMore }) => {
  const updates = useNotificationsStore((state) => state.notifications.updates);
  const markAsRead = useNotificationsStore((state) => state.markAsRead);
  const markAllAsRead = useNotificationsStore((state) => state.markAllAsRead);
  const clearNotification = useNotificationsStore(
    (state) => state.clearNotification
  );
  const scrollRef = useRef(null);

  const handleMarkAsRead = async (id) => {
    try {
      const response = await notificationService.markNotificationAsRead(id); // Update on backend
      if (response.status === 200) {
        markAsRead(id);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleClear = async (id) => {
    try {
      const response = await notificationService.clearNotification(id);
      if (response.status === 200) {
        clearNotification(id);
      }
    } catch (error) {
      console.error('Failed to clear:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationService.markAllAsRead('update');
      if (response.status === 200) {
        markAllAsRead();
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchMore();
    }
  }, [fetchMore, loadingMore, hasMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      handleMarkAllAsRead();
    };
  }, []);

  return (
    <div ref={scrollRef} className="flex flex-col max-h-[400px] overflow-y-auto scrollbar">
      {updates.length > 0 ? (
        <div className="flex flex-col w-full">
          {updates.map((task) => (
            <div
              key={task.id}
              className="flex flex-col justify-center items-start gap-v3-ms p-v3-sm self-stretch"
            >
              <div className="flex justify-between items-center self-stretch">
                <p className="text-v3-h11 font-medium text-v3-black-secondary leading-4">
                  {DateTime.fromISO(task.date_created, {
                    zone: 'utc',
                  }).toRelative()}
                </p>
                <button
                  onClick={() => handleClear(task.id)}
                  className="cursor-pointer"
                >
                  <IconWrapper size={16}>
                    <CloseIconMenu className="w-full h-full text-v3-black-secondary hover:text-v3-black-max stroke-2" />
                  </IconWrapper>
                </button>
              </div>
              <p className="text-sm font-regular text-v3-black-max leading-[22px]">
                {task.text}
              </p>
              {task.link && (
                <a
                  className="text-xs text-grey-50 flex items-center gap-v3-xxs"
                  href={task.link}
                >
                  {task.link_text} <ChevronRightIcon width={12} height={12} />
                </a>
              )}
            </div>
          ))}
          {loadingMore && (
            <div className="flex justify-center py-v3-sm">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-v3-purple-primary border-t-transparent" />
            </div>
          )}
        </div>
      ) : (
        <div className="min-h-36 flex flex-col items-center justify-center gap-v3-xs py-v3-lg">
          <div className="h-28 flex justify-center items-center">
            <NotificationIcon />
          </div>
          <div className="flex flex-col gap-v3-xxs text-center">
            <p className="text-xs text-grey-50">No unread updates</p>
            <p className="text-2xs text-grey-50">
              New updates will be shown here
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DNotificationBlockUpdates;
